import { buildCardCountdown } from "@/utils/vip-access.utils";
import { formatBrDateTime, formatBrTime } from "@/utils/br-datetime";

export type CheckInDayCardTiming = {
  timeLeft: string;
  countdown?: {
    days: number;
    hours: number;
    minutes: number;
    seconds?: number;
  };
  statusLabel: string;
  statusVariant: "success" | "warning" | "info";
  meta?: string;
};

/** Texto e contagem para cards do hub “Meu Dia”. */
export function getCheckInDayCardTiming(input: {
  eventStartsAt: string;
  eventEndsAt?: string | null;
  hasLocationCoordinates: boolean;
  checkedInAt?: string | null;
  countsForRanking?: boolean;
  nowMs?: number;
}): CheckInDayCardTiming | null {
  if (input.checkedInAt) return null;

  if (!input.hasLocationCoordinates) {
    return {
      timeLeft: "Local sem mapa",
      statusLabel: "Check-in indisponível",
      statusVariant: "warning",
      meta: "Endereço do jogo ainda não está no mapa",
    };
  }

  const nowMs = input.nowMs ?? Date.now();
  const state = getCheckInWindowState(
    input.eventStartsAt,
    input.eventEndsAt,
    nowMs,
  );

  if (state.isAfterDeadline) return null;

  const deadlineIso = new Date(state.deadlineMs).toISOString();

  if (state.isOpen) {
    return {
      timeLeft:
        input.countsForRanking && state.isEarlyBonusPeriod
          ? "Check-in aberto · bônus no ranking"
          : "Check-in aberto agora",
      statusLabel: "Check-in aberto",
      statusVariant: "success",
      meta: `Até ${formatBrTime(deadlineIso)}`,
    };
  }

  if (state.isBeforeOpen) {
    const opensIso = new Date(state.opensAtMs).toISOString();
    return {
      timeLeft: `Check-in às ${formatBrTime(opensIso)}`,
      countdown: buildCardCountdown(new Date(state.opensAtMs), new Date(nowMs)),
      statusLabel: "Check-in em breve",
      statusVariant: "info",
      meta: `Abre ${formatBrDateTime(opensIso, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}`,
    };
  }

  return null;
}

export const CHECK_IN_EARLY_MS = 15 * 60 * 1000;
export const CHECK_IN_GRACE_AFTER_END_MS = 30 * 60 * 1000;
export const CHECK_IN_EARLY_BONUS_MULTIPLIER = 2;

export function getCheckInWindowState(
  startsAt: string,
  endsAt?: string | null,
  nowMs: number = Date.now(),
) {
  const startMs = new Date(startsAt).getTime();
  const endMs = endsAt ? new Date(endsAt).getTime() : startMs;
  const openMs = startMs - CHECK_IN_EARLY_MS;
  const deadlineMs = endMs + CHECK_IN_GRACE_AFTER_END_MS;
  const isOpen = nowMs >= openMs && nowMs <= deadlineMs;
  const isEarlyBonusPeriod = isOpen && nowMs < startMs;
  return {
    isOpen,
    isBeforeOpen: nowMs < openMs,
    isAfterDeadline: nowMs > deadlineMs,
    isEarlyBonusPeriod,
    opensAtMs: openMs,
    deadlineMs,
  };
}

/** Ex.: "Check-in disponível em 4h e 32 min" */
export function formatCheckInOpensIn(
  opensAtMs: number,
  nowMs: number = Date.now(),
): string {
  const diffMs = Math.max(0, opensAtMs - nowMs);
  const totalMinutes = Math.ceil(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `Check-in disponível em ${hours}h e ${minutes} min`;
  }
  if (hours > 0) {
    return `Check-in disponível em ${hours}h`;
  }
  if (minutes > 0) {
    return `Check-in disponível em ${minutes} min`;
  }
  return "Check-in disponível em instantes";
}

export function canOpenGameCheckIn(startsAt: string, nowMs: number = Date.now()) {
  return nowMs >= new Date(startsAt).getTime() - CHECK_IN_EARLY_MS;
}

export function canPerformEventSelfCheckIn(input: {
  eventStartsAt: string;
  eventEndsAt?: string | null;
  hasLocationCoordinates: boolean;
  checkedInAt?: string | null;
  nowMs?: number;
}): boolean {
  if (input.checkedInAt) return false;
  if (!input.hasLocationCoordinates) return false;
  return getCheckInWindowState(
    input.eventStartsAt,
    input.eventEndsAt,
    input.nowMs ?? Date.now(),
  ).isOpen;
}

/** Exibe aviso de check-in antes da janela abrir; some após o prazo ou se já fez check-in. */
export function shouldShowEventCheckInPrompt(input: {
  eventStartsAt: string;
  eventEndsAt?: string | null;
  hasLocationCoordinates: boolean;
  checkedInAt?: string | null;
  nowMs?: number;
}): boolean {
  if (input.checkedInAt) return false;
  if (!input.hasLocationCoordinates) return false;
  return !getCheckInWindowState(
    input.eventStartsAt,
    input.eventEndsAt,
    input.nowMs ?? Date.now(),
  ).isAfterDeadline;
}
